import React, { useState, useRef, useEffect, useCallback } from 'react';

const STATUS_CATEGORY_COLORS = {
  'new': '#42526E',
  'indeterminate': '#0065FF',
  'done': '#006644',
  'undefined': '#6B778C',
};

function StatusEditor({ value, onChange, disabled, transitions, loading, savingId }) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef(null);

  // Auto-focus the wrapper for keyboard navigation
  useEffect(() => {
    if (listRef.current) {
      listRef.current.focus();
    }
  }, []);

  const currentStatusName = value?.name?.toLowerCase() || '';
  const filtered = (transitions || []).filter(t => {
    const targetName = (t.to?.name || '').toLowerCase();
    if (targetName && targetName === currentStatusName) return false;
    return true;
  });

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.field-editor-option');
    if (items[highlightedIndex]) {
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleKeyDown = useCallback((e) => {
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? filtered.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        if (!disabled) onChange(filtered[highlightedIndex]);
      }
    }
  }, [filtered, highlightedIndex, disabled, onChange]);

  return (
    <div
      className="field-editor-status-wrapper"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      ref={listRef}
    >
      {loading ? (
        <div className="field-editor-loading">Loading transitions...</div>
      ) : (
        <div className="field-editor-options-list">
          {filtered.map((t, idx) => {
            const categoryKey = t.to?.statusCategory?.key || 'undefined';
            const color = STATUS_CATEGORY_COLORS[categoryKey] || 'var(--accent)';
            const isSaving = savingId === t.id;
            return (
              <div
                key={t.id}
                className={`field-editor-option field-editor-status-option ${highlightedIndex === idx ? 'highlighted' : ''} ${isSaving ? 'saving' : ''}`}
                onClick={() => !disabled && onChange(t)}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {isSaving ? (
                  <span className="field-editor-status-spinner" />
                ) : (
                  <span
                    className="field-editor-status-dot"
                    style={{ backgroundColor: color }}
                  />
                )}
                <span className="field-editor-option-label">{t.to?.name || t.name}</span>
                <span className="field-editor-status-target">
                  {t.name}
                </span>
              </div>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div className="field-editor-empty">No transitions found</div>
          )}
        </div>
      )}
    </div>
  );
}

export default StatusEditor;
